import { Injectable } from '@angular/core';
import { DocumentResource, Resource, Service } from 'ngx-jsonapi';
import { lastValueFrom } from 'rxjs';

import { Event } from './event.service';

export type ManagedAvailabilityStatus = 'available' | 'unavailable' | 'booked';

export interface ManagedAvailabilityEntry {
  id: string;
  date: string;
  status: ManagedAvailabilityStatus;
  sourceEventId: string | null;
  sourceEventTitle: string;
  isPast: boolean;
}

export interface AvailabilityManagementState {
  entries: ManagedAvailabilityEntry[];
}

export class AvailabilityEntryResource extends Resource {
  public override attributes = {
    date: '',
    status: 1,
    source_event_title: '',
    is_past: false,
    notes: '',
    created_at: '',
    updated_at: '',
  };

  public override relationships = {
    source_event: new DocumentResource<Event>(),
  };
}

@Injectable({
  providedIn: 'root',
})
export class ProfileAvailabilityService extends Service<AvailabilityEntryResource> {
  public override resource = AvailabilityEntryResource;
  public override type = 'AvailabilityEntry';
  public override path = '/api/availability-entries';

  public async fetchState(): Promise<AvailabilityManagementState> {
    const page = await lastValueFrom(this.all({
      page: {
        number: 1,
        size: 400,
      },
    }));

    return {
      entries: page.data.map((entry) => this.toManagedEntry(entry)),
    };
  }

  public async addDate(date: string): Promise<AvailabilityManagementState> {
    const entry = this.new();
    entry.attributes.date = date;
    entry.attributes.status = 1;
    entry.attributes.notes = '';

    await lastValueFrom(entry.save());
    return this.fetchState();
  }

  public async updateEntries(
    entryIds: string[],
    status: Exclude<ManagedAvailabilityStatus, 'booked'>,
  ): Promise<AvailabilityManagementState> {
    const nextStatus = this.toApiStatus(status);
    const entries = await Promise.all(
      entryIds.map((entryId) => lastValueFrom(this.get(entryId))),
    );

    await Promise.all(entries.map(async (entry) => {
      entry.attributes.status = nextStatus;
      await lastValueFrom(entry.save());
    }));

    return this.fetchState();
  }

  public async deleteEntries(entryIds: string[]): Promise<AvailabilityManagementState> {
    await Promise.all(entryIds.map((entryId) => lastValueFrom(this.delete(entryId))));
    return this.fetchState();
  }

  private toManagedEntry(entry: AvailabilityEntryResource): ManagedAvailabilityEntry {
    return {
      id: entry.id,
      date: entry.attributes.date,
      status: this.toManagedStatus(entry.attributes.status),
      sourceEventId: entry.relationships.source_event.data?.id ?? null,
      sourceEventTitle: entry.attributes.source_event_title,
      isPast: entry.attributes.is_past,
    };
  }

  private toManagedStatus(status: number): ManagedAvailabilityStatus {
    if (status === 2) {
      return 'unavailable';
    }
    if (status === 3) {
      return 'booked';
    }
    return 'available';
  }

  private toApiStatus(status: Exclude<ManagedAvailabilityStatus, 'booked'>): number {
    return status === 'unavailable' ? 2 : 1;
  }
}
